import { Component, PhysXComponent, Property, TextComponent } from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';
import { ArenaHandler } from './arena-handler';
import { Arena } from './net/arena.js';

/**
 * BoostPurchaseHandler
 * Manages in-game purchases for a singular (non-stackable) item.
 */
export class BoostPurchaseHandler extends Component {
    static TypeName = 'boost-purchase-handler';
    
    static Properties = {
        itemSlug: Property.string('xp_boost'),
        arenaHandlerObj: Property.object(),
        buyButtonObj: Property.object(),
        statusTextObj: Property.object(),
    };

    start() {
        // 1. JS-to-WASM Bridge Efficiency: Cache component references once
        this.networkHandler = this.arenaHandlerObj.getComponent(ArenaHandler);
        this.info = this.statusTextObj.getComponent(TextComponent);
        this.col = this.buyButtonObj.getComponent(PhysXComponent);
        
        if (this.info) this.info.text = "Checking status...";

        // NEW: State for confirmation click
        this._isConfirming = false;
        this._confirmTimeout = null;

        // 2. Math Operations & GC: Use flat Float32Array which natively maps to WASM memory
        // This avoids glMatrix object wrapper allocation overhead and works natively with engine
        this._initialPosition = new Float32Array(3);
        this.object.getPositionWorld(this._initialPosition);
        
        this.col.active = false; // Hide the buy button until we check status

        // 3. GC Elimination: Pre-bind methods to avoid closure allocations in timeouts
        this._boundPurchaseSuccess = this._handlePurchaseSuccessState.bind(this);
        this._boundInitCatch = this._handleInitError.bind(this);
        this._boundResetConfirm = this._resetConfirmState.bind(this); // NEW: Bind the reset function

        // Wait for REST auth before checking login (no-op if already ready).
        Arena.ready.then(() => this.checkLoggedInStatus().catch(this._boundInitCatch));
    }
    
    _handleInitError() {
        // Silently catch to avoid synchronous console I/O blocking
    }

    _resetConfirmState() {
        if (this._isConfirming) {
            this._isConfirming = false;
            if (this.info) this.info.text = "Buy";
        }
    }

    async checkLoggedInStatus() {
        try {
            const name = await Arena.user.getName();
            this._isUserLoggedIn = !!name;

            if (!this._isUserLoggedIn) {
                if (this.info) this.info.text = "Login required!";
            } else {
                this.checkInitialStatus();
            }
        } catch (e) {
            if (this.info) this.info.text = "Login required!";
            this._isUserLoggedIn = false;
        }
    }

    async checkInitialStatus() {
        const isOwned = await this.verifyPurchase();
        
        if (isOwned) {
            if (this.info) this.info.text = "Boost Active!";
            if (this.networkHandler) this.networkHandler._updateXpBoostStatus(true);
            this.col.active = false;
        } else {
            // If they are logged in but don't own it, prompt to buy
            if (this._isUserLoggedIn && this.info) {
                this.info.text = "Buy";
                this.col.active = true;
            }
        }
    }

    async onPress() {
        // 1. Strict guardrail: Prevent guest accounts from triggering API calls
        if (!this._isUserLoggedIn) {
            if (this.info) this.info.text = "Login required!";
            return;
        }

        // --- NEW: CONFIRMATION LOGIC ---
        if (!this._isConfirming) {
            // Pre-check: Don't allow purchase if already owned
            const alreadyOwned = await this.verifyPurchase();
            if (alreadyOwned) {
                if (this.info) this.info.text = "Already Active!";
                this.col.active = false; // Safe to disable here
                return;
            }

            // Enter confirmation state
            this._isConfirming = true;
            if (this.info) this.info.text = "Confirm";
            
            // Reset back to "Buy" after 10 seconds if they don't confirm
            if (this._confirmTimeout) clearTimeout(this._confirmTimeout);
            this._confirmTimeout = setTimeout(this._boundResetConfirm, 3500);
            
            return; // Stop execution here and wait for the second click
        }

        // --- PROCESSING CONFIRMED PURCHASE ---
        
        // Clear the timeout and reset state since they clicked again
        if (this._confirmTimeout) clearTimeout(this._confirmTimeout);
        this._isConfirming = false;
        
        this.col.active = false; // Disable button to prevent multi-clicking while processing
        if (this.info) this.info.text = "Processing...";

        try {
            // 3. Process the Purchase
            await Arena.inventory.purchase(this.itemSlug);

            // 4. Final Verification to ensure the backend registered it
            const isVerified = await this.verifyPurchase();
            
            if (isVerified) {
                if (this.info) this.info.text = "Success!";
                this.onPurchaseSuccess();
            } else {
                if (this.info) this.info.text = "Pending...";
            }

        } catch (error) {
            // Check for specific error codes from the inventory API
            const statusCode = error.status ? (error.status.code || error.status) : null;
            const debugMsg = error.status ? error.status.debug : null;

            if (statusCode === 401) {
                if (this.info) this.info.text = "Login required!";
                this._isUserLoggedIn = false;
            } else if (statusCode === 402 || debugMsg === 'err_insufficient_balance') {
                if (this.info) this.info.text = "Not enough coins!";
                this.col.active = true; // Allow them to try again after seeing the message
            } else {
                // Generic fallback for network errors or other issues
                if (this.info) this.info.text = "Purchase failed!"; 
                this.col.active = true; // Let them try again
            }
        }
    }

    async verifyPurchase() {
        // Early exit to prevent 401 Unauthorized API spam for guests
        if (!this._isUserLoggedIn) return false;

        try {
            // Retrieve game items owned by user
            const inventory = await Arena.inventory.get();
            let item = null;
            
            // 4. CPU Efficiency & GC Elimination: 
            // Replaced `inventory.find(i => ...)` with a standard for-loop to prevent runtime closure allocation
            for (let i = 0; i < inventory.length; i++) {
                if (inventory[i].slug === this.itemSlug) {
                    item = inventory[i];
                    break;
                }
            }

            // If found and count is 1 or more, they own it
            if (item && item.owned_count > 0) {
                if (item.expires_on) {
                    // GC Elimination: Replaced `new Date()` object instantiation with primitive number (Date.now())
                    // and Date.parse() to evaluate strings without creating intermediate Date objects.
                    const dateNow = Date.now();
                    const cleanDateString = item.expires_on.replace(/(\.\d{3})\d+/, '$1');
                    if (Date.parse(cleanDateString) < dateNow) {
                        return false;
                    }
                }
                return true;
            }
        } catch (e) {
            // Silently fail, avoiding synchronous GC-heavy console.warn logging in production
        }
        return false;
    }

    onPurchaseSuccess() {
        if (this.networkHandler) {
            this.networkHandler._updateXpBoostStatus(true);
        }
        // GC Elimination: Uses the pre-bound function instead of an anonymous inline `() => {}` closure
        setTimeout(this._boundPurchaseSuccess, 1500);
    }
    
    _handlePurchaseSuccessState() {
        if (this.info) this.info.text = "Boost Active!";
    }

    _resetSession() {
        this.col.active = true;
        if (this.object && this._initialPosition) {
            this.object.setPositionWorld(this._initialPosition);
        }
        if (this.buyButtonObj) this.buyButtonObj.active = true;
        this.checkInitialStatus();
    }
}